import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { useTranslation } from 'react-i18next';
import BGGAttribution from '../components/BGGAttribution';
import BGGSearch from '../components/BGGSearch';
import EditShelfEntryDialog from '../components/games/EditShelfEntryDialog';
import GameFiltersBar, {
  defaultGameFilters,
  defaultGameSort,
} from '../components/games/GameFiltersBar';
import GameDetailsDialog from '../components/games/GameDetailsDialog';
import ShelfSection from '../components/games/ShelfSection';
import { useToast } from '../components/tavern/ToastProvider';
import {
  addShelfGame,
  deleteShelfGame,
  updateShelfGame,
} from '../api/library';

const defaultManualGame = {
  title: '',
  minPlayers: '',
  maxPlayers: '',
  playingTime: '',
  weight: '',
  notes: '',
  coverImage: '',
};

const MANUAL_COVER_MAX_BYTES = 600 * 1024;
const MANUAL_COVER_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const GameShelf = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [manualGame, setManualGame] = useState(defaultManualGame);
  const [manualEntryVisible, setManualEntryVisible] = useState(false);
  const [manualCoverError, setManualCoverError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [ownedGamesCount, setOwnedGamesCount] = useState(0);
  const [filters, setFilters] = useState(defaultGameFilters);
  const [sort, setSort] = useState(defaultGameSort);
  const [friendSearchQuery, setFriendSearchQuery] = useState('');
  const [friendFilters, setFriendFilters] = useState(defaultGameFilters);
  const [friendSort, setFriendSort] = useState(defaultGameSort);
  const activeFilters = useMemo(() => filters, [filters]);
  const activeFriendFilters = useMemo(() => friendFilters, [friendFilters]);
  const selectedGameIsFromCompanion = Boolean(selectedGame?.ownerId);

  const refreshShelf = () => setRefreshKey((current) => current + 1);

  const handleSelectBggGame = async (bggGame) => {
    try {
      setIsSaving(true);
      await addShelfGame({
        source: 'bgg',
        bggId: bggGame.id,
        personalRating: null,
      });
      closeAddModal();
      refreshShelf();
      toast.success(t('shelf.feedback.addedBgg'));
    } catch (error) {
      toast.error(t('shelf.feedback.addBggError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualChange = (field) => (event) => {
    setManualGame((current) => ({ ...current, [field]: event.target.value }));
  };

  const resetAddModal = () => {
    setManualGame(defaultManualGame);
    setManualEntryVisible(false);
    setManualCoverError('');
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetAddModal();
  };

  const handleManualCoverChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setManualCoverError('');

    if (!file) return;

    if (!MANUAL_COVER_TYPES.includes(file.type)) {
      setManualCoverError(t('shelf.modal.cover.invalidType'));
      return;
    }

    if (file.size > MANUAL_COVER_MAX_BYTES) {
      setManualCoverError(t('shelf.modal.cover.tooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setManualGame((current) => ({ ...current, coverImage: reader.result }));
      }
    };
    reader.onerror = () => setManualCoverError(t('shelf.modal.cover.readError'));
    reader.readAsDataURL(file);
  };

  const handleManualSubmit = async () => {
    if (!manualGame.title.trim()) return;

    try {
      setIsSaving(true);
      await addShelfGame({
        source: 'manual',
        title: manualGame.title,
        minPlayers: manualGame.minPlayers,
        maxPlayers: manualGame.maxPlayers,
        playingTime: manualGame.playingTime,
        weight: manualGame.weight,
        notes: manualGame.notes,
        image: manualGame.coverImage || undefined,
        thumbnail: manualGame.coverImage || undefined,
      });
      closeAddModal();
      refreshShelf();
      toast.success(t('shelf.feedback.addedManual'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEntry = async (values) => {
    if (!editingGame) return;

    try {
      setIsSaving(true);
      const updated = await updateShelfGame(editingGame.id, values);
      setEditingGame(null);
      setSelectedGame(updated);
      refreshShelf();
      toast.success(t('shelf.feedback.updated'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (game) => {
    await deleteShelfGame(game.id);
    setSelectedGame(null);
    refreshShelf();
    toast.success(t('shelf.feedback.removed'));
  };

  return (
    <Box sx={{ py: { xs: 1, md: 2 } }}>
      <Stack spacing={5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
              {t('shelf.eyebrow')}
            </Typography>
            <Typography variant="h1">{t('shelf.title')}</Typography>
            <Typography color="text.secondary">
              {t('shelf.subtitle', { count: ownedGamesCount })}
            </Typography>
          </Box>
        </Stack>

        <GameFiltersBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFiltersChange={setFilters}
          sort={sort}
          onSortChange={setSort}
        />

        <ShelfSection
          key={refreshKey}
          limit={12}
          search={searchQuery}
          filters={activeFilters}
          sort={sort}
          onAdd={() => setShowAddModal(true)}
          onSelect={setSelectedGame}
          onTotalChange={setOwnedGamesCount}
        />

        <Box>
          <Typography variant="h3">{t('shelf.friendsTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('shelf.friendsSubtitle')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <GameFiltersBar
              searchQuery={friendSearchQuery}
              onSearchChange={setFriendSearchQuery}
              filters={friendFilters}
              onFiltersChange={setFriendFilters}
              sort={friendSort}
              onSortChange={setFriendSort}
              searchPlaceholder={t('shelf.friendsSearchPlaceholder')}
            />
          </Box>
          <ShelfSection
            source="friends"
            limit={8}
            search={friendSearchQuery}
            filters={activeFriendFilters}
            sort={friendSort}
            onSelect={setSelectedGame}
          />
        </Box>

        <BGGAttribution mode="footer" />
      </Stack>

      <Dialog
        open={showAddModal}
        onClose={closeAddModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('shelf.modal.title')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <BGGSearch onSelect={handleSelectBggGame} disabled={isSaving} />

            {!manualEntryVisible ? (
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  border: '1px dashed',
                  borderColor: 'rgba(15, 76, 69, 0.24)',
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 250, 240, 0.52)',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setManualEntryVisible(true)}
                  sx={{
                    justifyContent: 'center',
                    width: { xs: '100%', sm: 'auto' },
                    borderWidth: 2,
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(15, 76, 69, 0.06)',
                    fontWeight: 900,
                    px: 2.25,
                    py: 1.1,
                    '&:hover': {
                      borderWidth: 2,
                      bgcolor: 'rgba(15, 76, 69, 0.12)',
                    },
                  }}
                >
                  {t('shelf.modal.showManual')}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('shelf.modal.manualHint')}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h3">{t('shelf.modal.manualTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('shelf.modal.manualSubtitle')}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label={t('shelf.modal.fields.title')}
                    value={manualGame.title}
                    onChange={handleManualChange('title')}
                  />
                  <TextField
                    label={t('shelf.modal.fields.weight')}
                    placeholder={t('shelf.modal.fields.weightPlaceholder')}
                    value={manualGame.weight}
                    onChange={handleManualChange('weight')}
                  />
                  <TextField
                    label={t('shelf.modal.fields.minPlayers')}
                    type="number"
                    value={manualGame.minPlayers}
                    onChange={handleManualChange('minPlayers')}
                  />
                  <TextField
                    label={t('shelf.modal.fields.maxPlayers')}
                    type="number"
                    value={manualGame.maxPlayers}
                    onChange={handleManualChange('maxPlayers')}
                  />
                  <TextField
                    label={t('shelf.modal.fields.playingTime')}
                    type="number"
                    value={manualGame.playingTime}
                    onChange={handleManualChange('playingTime')}
                  />
                  <TextField
                    label={t('shelf.modal.fields.notes')}
                    value={manualGame.notes}
                    onChange={handleManualChange('notes')}
                  />
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 112,
                      aspectRatio: '3 / 4',
                      border: '1px dashed',
                      borderColor: 'rgba(15, 76, 69, 0.28)',
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                      bgcolor: 'rgba(15, 76, 69, 0.06)',
                      color: 'text.secondary',
                      flex: '0 0 auto',
                    }}
                  >
                    {manualGame.coverImage ? (
                      <Box
                        component="img"
                        src={manualGame.coverImage}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ImageOutlinedIcon />
                    )}
                  </Box>
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant="outlined" component="label">
                        {t('shelf.modal.cover.upload')}
                        <input
                          type="file"
                          hidden
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleManualCoverChange}
                        />
                      </Button>
                      {manualGame.coverImage ? (
                        <Button
                          variant="text"
                          onClick={() =>
                            setManualGame((current) => ({ ...current, coverImage: '' }))
                          }
                        >
                          {t('shelf.modal.cover.remove')}
                        </Button>
                      ) : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t('shelf.modal.cover.helper')}
                    </Typography>
                    {manualCoverError ? (
                      <Alert severity="error" variant="outlined">
                        {manualCoverError}
                      </Alert>
                    ) : null}
                  </Stack>
                </Stack>

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled={!manualGame.title.trim() || isSaving}
                  onClick={handleManualSubmit}
                >
                  {t('shelf.modal.addManual')}
                </Button>
              </Stack>
            )}
            <BGGAttribution mode="inline" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            startIcon={<CloseIcon />}
            onClick={closeAddModal}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <GameDetailsDialog
        open={Boolean(selectedGame)}
        game={selectedGame}
        onClose={() => setSelectedGame(null)}
        onEdit={selectedGameIsFromCompanion ? undefined : setEditingGame}
        onDelete={selectedGameIsFromCompanion ? undefined : handleDelete}
      />
      <EditShelfEntryDialog
        open={Boolean(editingGame)}
        game={editingGame}
        isSaving={isSaving}
        onClose={() => setEditingGame(null)}
        onSave={handleSaveEntry}
      />
    </Box>
  );
};

export default GameShelf;
